/* ══════════════════════════════════════════════════════════════════════════

   PLAZOS LEGALES — cómputo de días hábiles y vencimientos
   ══════════════════════════════════════════════════════════════════════════

   Calcula, a partir de los datos que ya tiene la ficha del caso, cuándo vence
   el plazo para interponer la demanda o para contestarla. Lo consume el bloque
   "Plazos legales" de Notificaciones.

   Reglas de cómputo acordadas con el estudio:

   • DÍAS HÁBILES = lunes a sábado. El sábado CUENTA. Son inhábiles los
     domingos y los feriados legales chilenos. Es el criterio del art. 435 del
     Código del Trabajo en relación al art. 66 del Código de Procedimiento
     Civil, y el que publica la Dirección del Trabajo para el plazo del
     art. 168 ("60 días hábiles, de lunes a sábado, excluyendo festivos").

   • El día del hecho NO se cuenta: el primer día hábil del plazo es el
     siguiente al despido, a la separación o a la notificación.

   • Los plazos de MESES son de meses corridos (art. 48 del Código Civil). Si
     el vencimiento cae en domingo o feriado se muestra el día hábil ANTERIOR,
     que es la última fecha en que efectivamente se puede presentar.

   • El plazo del art. 168 SE SUSPENDE por el reclamo ante la Inspección: deja
     de correr el día en que se solicita el comparendo y vuelve a correr con el
     comparendo mismo. Las dos fechas salen del comparendo registrado en
     "Audiencias y fechas clave". Sobre todo ello manda el tope del inciso
     final: nunca más de 90 días hábiles desde la separación.

   Nada de esto adivina: si al caso le falta la fecha que hace correr el plazo,
   ese plazo simplemente no se muestra. Un vencimiento inventado es peor que
   ninguno.
   ══════════════════════════════════════════════════════════════════════════ */
import { audienciaVigente } from './audiencias';

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Fecha local a medianoche, sin hora. Todo el módulo trabaja con estas. */
const soloDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const sumarDias = (d: Date, n: number) => {
  const x = soloDia(d);
  x.setDate(x.getDate() + n);
  return x;
};
export const claveDia = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** Lee "YYYY-MM-DD" o "DD-MM-YYYY" a Date local. Devuelve null si no es fecha.
 *  Pasarla por new Date() la leería como UTC y en Chile daría el día anterior:
 *  un plazo corrido un día es un plazo perdido. */
export function parseFecha(valor: any): Date | null {
  const s = String(valor || '').trim();
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  return null;
}

export const fmtDia = (d: Date) => `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;

/* ── Feriados legales de Chile ──────────────────────────────────────────── */

/** Domingo de Pascua (algoritmo de Meeus/Jones/Butcher, calendario gregoriano).
 *  De él salen el Viernes y el Sábado Santo, que son feriados legales. */
export function domingoDePascua(anio: number): Date {
  const a = anio % 19;
  const b = Math.floor(anio / 100);
  const c = anio % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mes = Math.floor((h + l - 7 * m + 114) / 31);
  const dia = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(anio, mes - 1, dia);
}

/** Traslado de la Ley 19.973 (San Pedro y San Pablo, 29 de junio; Encuentro de
 *  Dos Mundos, 12 de octubre): si cae martes, miércoles o jueves se celebra el
 *  lunes de esa misma semana; si cae viernes, el lunes siguiente. Lunes,
 *  sábado y domingo no se trasladan. */
function trasladoALunes(f: Date): Date {
  const dow = f.getDay();
  if (dow >= 2 && dow <= 4) return sumarDias(f, 1 - dow);
  if (dow === 5) return sumarDias(f, 3);
  return f;
}

/** Traslado de la Ley 20.299 (Día de las Iglesias Evangélicas, 31 de octubre):
 *  si cae martes se celebra el viernes de la semana anterior; si cae miércoles,
 *  el viernes de esa misma semana. Los demás días no se trasladan. */
function trasladoAViernes(f: Date): Date {
  const dow = f.getDay();
  if (dow === 2) return sumarDias(f, -4);
  if (dow === 3) return sumarDias(f, 2);
  return f;
}

/** Día Nacional de los Pueblos Indígenas (Ley 21.357): el solsticio de invierno,
 *  que cae el 20 o el 21 de junio según el año. Va en tabla porque depende de
 *  la hora exacta del solsticio en Chile, no de una regla de calendario.
 *  Fuera de la tabla se asume el 21, que es el caso más frecuente. */
const SOLSTICIO_JUNIO: Record<number, number> = {
  2021: 21, 2022: 21, 2023: 21, 2024: 20, 2025: 20,
  2026: 21, 2027: 21, 2028: 20, 2029: 20, 2030: 21,
};

/** Feriados de una sola vez (elecciones, feriados por ley especial) que NO caen
 *  domingo — los que caen domingo ya son inhábiles y no cambian ningún cómputo.
 *  Esta lista hay que ampliarla cuando se decrete uno nuevo. */
const FERIADOS_EXTRA = [
  '2021-05-15', // Elecciones de convencionales constituyentes (sábado)
  '2022-09-16', // Feriado de Fiestas Patrias, Ley 21.475 (viernes)
  '2024-10-26', // Elecciones municipales y regionales (sábado)
];

const cacheFeriados = new Map<number, Set<string>>();

/** Los feriados legales de un año, como claves "YYYY-MM-DD". */
export function feriadosDe(anio: number): Set<string> {
  const guardado = cacheFeriados.get(anio);
  if (guardado) return guardado;

  const pascua = domingoDePascua(anio);
  const dias: Date[] = [
    new Date(anio, 0, 1),                    // Año Nuevo
    sumarDias(pascua, -2),                   // Viernes Santo
    sumarDias(pascua, -1),                   // Sábado Santo
    new Date(anio, 4, 1),                    // Día del Trabajo
    new Date(anio, 4, 21),                   // Glorias Navales
    trasladoALunes(new Date(anio, 5, 29)),   // San Pedro y San Pablo
    new Date(anio, 6, 16),                   // Virgen del Carmen
    new Date(anio, 7, 15),                   // Asunción de la Virgen
    new Date(anio, 8, 18),                   // Independencia Nacional
    new Date(anio, 8, 19),                   // Glorias del Ejército
    trasladoALunes(new Date(anio, 9, 12)),   // Encuentro de Dos Mundos
    trasladoAViernes(new Date(anio, 9, 31)), // Iglesias Evangélicas y Protestantes
    new Date(anio, 10, 1),                   // Todos los Santos
    new Date(anio, 11, 8),                   // Inmaculada Concepción
    new Date(anio, 11, 25),                  // Navidad
  ];
  // Existe desde la Ley 21.357 (2021); antes de eso no era feriado.
  if (anio >= 2021) dias.push(new Date(anio, 5, SOLSTICIO_JUNIO[anio] ?? 21));
  // Ley 20.215: si el 18 de septiembre cae martes, el lunes 17 es feriado; si
  // el 19 cae jueves (o sea, el 18 miércoles), lo es el viernes 20. Es el
  // feriado que más se olvida, y cae justo donde más plazos vencen.
  if (anio >= 2007) {
    const dieciocho = new Date(anio, 8, 18).getDay();
    if (dieciocho === 2) dias.push(new Date(anio, 8, 17));
    if (dieciocho === 3) dias.push(new Date(anio, 8, 20));
  }

  const set = new Set(dias.map(claveDia));
  for (const extra of FERIADOS_EXTRA) if (extra.startsWith(String(anio))) set.add(extra);
  cacheFeriados.set(anio, set);
  return set;
}

export const esFeriado = (d: Date) => feriadosDe(d.getFullYear()).has(claveDia(d));

/** Día hábil = lunes a sábado que no sea feriado legal. */
export const esHabil = (d: Date) => d.getDay() !== 0 && !esFeriado(d);

/** El día hábil anterior a `d` (o `d` mismo si ya es hábil). */
export function habilAnterior(d: Date): Date {
  let x = soloDia(d);
  let guarda = 0;
  while (!esHabil(x) && guarda++ < 30) x = sumarDias(x, -1);
  return x;
}

/** Vencimiento de un plazo de `n` días hábiles contados desde `desde`.
 *  El día del hecho no se cuenta: el día 1 es el siguiente día hábil. */
export function vencimientoHabil(desde: Date, n: number): Date {
  let d = soloDia(desde);
  let quedan = n;
  while (quedan > 0) {
    d = sumarDias(d, 1);
    if (esHabil(d)) quedan--;
  }
  return d;
}

/** El día que queda `n` días hábiles ANTES de `hasta` (el día de `hasta` no se
 *  cuenta). Es el último día para presentar un escrito que la ley exige "con a
 *  lo menos n días de antelación". */
export function limiteAnterior(hasta: Date, n: number): Date {
  let d = soloDia(hasta);
  let quedan = n;
  while (quedan > 0) {
    d = sumarDias(d, -1);
    if (esHabil(d)) quedan--;
  }
  return d;
}

/** Un tramo en que el plazo deja de correr. */
export type Suspension = { inicio: Date; fin: Date };

/** Como vencimientoHabil, pero sin contar los días que caen dentro de un tramo
 *  suspendido. Los tramos son cerrados: cuentan el día de inicio y el de fin. */
export function vencimientoHabilSuspendido(desde: Date, n: number, tramos: Suspension[]): Date {
  const suspendido = (x: Date) => tramos.some((t) => x >= t.inicio && x <= t.fin);
  let d = soloDia(desde);
  let quedan = n;
  let guarda = 0;
  while (quedan > 0 && guarda++ < 4000) {
    d = sumarDias(d, 1);
    if (esHabil(d) && !suspendido(d)) quedan--;
  }
  return d;
}

/** Vencimiento de un plazo de meses corridos (art. 48 del Código Civil),
 *  llevado al día hábil anterior si cayera en domingo o feriado. */
export function vencimientoMeses(desde: Date, meses: number): Date {
  const d = soloDia(desde);
  const objetivo = new Date(d.getFullYear(), d.getMonth() + meses, d.getDate());
  // Si el mes de destino tiene menos días (31 de agosto + 6 meses), el
  // constructor se pasa al mes siguiente. El art. 48 manda al último día del
  // mes de destino, que es lo que deja setDate(0).
  const mesEsperado = (((d.getMonth() + meses) % 12) + 12) % 12;
  if (objetivo.getMonth() !== mesEsperado) objetivo.setDate(0);
  return habilAnterior(objetivo);
}

/** Días hábiles que faltan desde hoy hasta `vence`, contando el día del
 *  vencimiento. 0 = vence hoy. Negativo = ya venció. */
export function habilesHasta(vence: Date, hoy: Date = new Date()): number {
  const h = soloDia(hoy);
  const v = soloDia(vence);
  if (v.getTime() === h.getTime()) return 0;
  const adelante = v > h;
  let d = h;
  let n = 0;
  let guarda = 0;
  while (d.getTime() !== v.getTime() && guarda++ < 4000) {
    d = sumarDias(d, adelante ? 1 : -1);
    if (esHabil(d)) n++;
  }
  return adelante ? n : -n;
}

/* ── Plazos de un caso ──────────────────────────────────────────────────── */

/** Quién dio por cumplido un plazo, y cuándo. */
export type PlazoRealizado = { fecha: string; por: string };

export type PlazoLegal = {
  /** Estable entre recargas: sirve de key en React. */
  id: string;
  /** Con qué llave se guarda el "realizado" en el caso. Lleva el vencimiento
   *  dentro a propósito: si se corrige la fecha de despido o la audiencia, el
   *  plazo cambia y vuelve a aparecer sin marcar, para que alguien lo
   *  reconfirme en vez de darlo por hecho sobre una fecha que ya no existe. */
  clave: string;
  casoId: string;
  codigoCaso: string;
  cliente: string;
  materia: 'Laboral' | 'Familia';
  clase: 'Demanda' | 'Contestación';
  titulo: string;
  norma: string;
  /** Cómo se calculó, en una línea, para que el abogado pueda auditarlo. */
  computo: string;
  desde: Date;
  vence: Date;
  /** Días hábiles restantes al momento del cálculo. */
  dias: number;
  /** null mientras nadie lo haya dado por cumplido. */
  realizado: PlazoRealizado | null;
  /** El rol de la causa cuando el caso ya está en tribunales. Un plazo para
   *  DEMANDAR de una causa con rol se da por cumplido solo: si hay rol es que
   *  la demanda ya se presentó (lo pidió svabogadas, 23-sep-2026). En ese
   *  caso `realizado` viene puesto y no se puede desmarcar desde la lista:
   *  para volver a verlo pendiente hay que borrar el rol del caso. Los plazos
   *  de CONTESTACIÓN no: esos existen justamente porque la causa ya tiene
   *  rol, y contestar es otra cosa que haber sido demandado. */
  judicializado: string | null;
};

/** Un caso en estos estados ya no tiene plazos que correr. */
const ESTADOS_CERRADOS = ['Resuelto / Cerrado', 'Desistido', 'Inviable'];

/** Cuántos días corridos sigue a la vista un plazo ya vencido. Esconderlo el
 *  mismo día en que vence es justo cuando más importa verlo. */
const DIAS_VISIBLE_TRAS_VENCER = 30;

const MOTIVO_AUTODESPIDO = 'Quiero autodespedirme';
const MOTIVO_DESPIDO = 'Me despidieron injustificadamente';
const MOTIVO_TUTELA = 'Sufrí acoso laboral o vulneración de derechos';
const MOTIVO_COTIZACIONES = 'Me deben cotizaciones o sueldos';

// Las suspendidas y reprogramadas no cuentan: vale su nueva fecha, que es otra audiencia.
const vigentes = (caso: any) => (caso.audiencias || []).filter(audienciaVigente);

const tipoAudiencia = (a: any) => String(a?.tipo || '').toUpperCase().replace(/[\s.]/g, '');

/** La primera audiencia preparatoria futura del caso, si la hay.
 *
 *  Solo la "A.P.". La "C.A.P" es la CONTINUACIÓN de la preparatoria: para
 *  entonces el plazo para contestar ya se cerró, así que anunciarlo ahí sería
 *  un aviso falso. Tampoco sirven la "A.J." ni la "C.A.J.". */
function proximaPreparatoria(caso: any, hoy: Date): Date | null {
  const h = soloDia(hoy);
  const fechas = vigentes(caso)
    .map((a: any) => {
      const tipo = tipoAudiencia(a);
      const esPreparatoria = tipo === 'AP' || (tipo.includes('PREPARATORIA') && !tipo.includes('CONTINUACION'));
      return esPreparatoria ? parseFecha(a?.fecha) : null;
    })
    .filter((f: Date | null): f is Date => !!f && f >= h)
    .sort((a: Date, b: Date) => a.getTime() - b.getTime());
  return fechas[0] || null;
}

/** Los tramos en que el plazo del art. 168 queda suspendido.
 *
 *  El reclamo ante la Inspección suspende el plazo, y la suspensión termina en
 *  el comparendo. Ambas fechas viven en el propio comparendo de "Audiencias y
 *  fechas clave": la del comparendo y la de cuándo se solicitó. Un comparendo
 *  sin fecha de solicitud no suspende nada — y eso adelanta el vencimiento que
 *  se muestra, que es el lado seguro del error. */
function suspensionesDelCaso(caso: any): Suspension[] {
  return vigentes(caso)
    .filter((a: any) => tipoAudiencia(a) === 'COMPARENDO')
    .map((a: any) => ({ inicio: parseFecha(a?.fechaSolicitud), fin: parseFecha(a?.fecha) }))
    .filter((t: any): t is Suspension => !!t.inicio && !!t.fin && t.fin >= t.inicio);
}

/** Los plazos legales vivos de un caso. Vacío si al caso le faltan los datos
 *  que hacen correr el plazo, o si ya está cerrado. */
export function plazosDelCaso(caso: any, hoy: Date = new Date()): PlazoLegal[] {
  if (!caso || ESTADOS_CERRADOS.includes(caso.estado)) return [];

  const tema = String(caso.tema || '').toLowerCase();
  const esLaboral = tema.includes('laboral');
  const esFamilia = tema.includes('familia');
  if (!esLaboral && !esFamilia) return [];

  const base = {
    casoId: caso.id,
    codigoCaso: caso.codigoCaso || '',
    cliente: caso.nombreCliente || caso.cliente || '—',
    materia: (esLaboral ? 'Laboral' : 'Familia') as 'Laboral' | 'Familia',
  };
  const marcados: Record<string, PlazoRealizado> = caso.plazosRealizados || {};
  // Todo rol o RIT de una causa lleva números (O-1234-2026, C-850-2025). Un
  // campo con solo letras ("Demandante", "pendiente") no prueba que la demanda
  // se haya presentado, y darla por judicializada escondería plazos vivos.
  const rolEscrito = String(caso.rolCaso || '').trim();
  const rol = /\d/.test(rolEscrito) ? rolEscrito : '';
  const plazos: PlazoLegal[] = [];
  const agregar = (
    clave: string, clase: PlazoLegal['clase'], titulo: string, norma: string,
    computo: string, desde: Date, vence: Date,
  ) => {
    const guardada = `${clave}@${claveDia(vence)}`;
    const judicializado = clase === 'Demanda' && rol ? rol : null;
    plazos.push({
      ...base, id: `${caso.id}:${clave}`, clave: guardada, clase, titulo, norma, computo,
      desde, vence, dias: habilesHasta(vence, hoy),
      realizado: marcados[guardada] || (judicializado ? { fecha: '', por: `rol ${judicializado}` } : null),
      judicializado,
    });
  };

  if (esLaboral) {
    const despido = parseFecha(caso.fechaDespido);
    const motivos: string[] = caso.laboralMotivos || [];

    // El plazo del art. 168: 60 días hábiles desde la separación, que dejan de
    // correr entre la solicitud del comparendo y el comparendo mismo, y que en
    // ningún caso pueden pasar del día 90 hábil (art. 168 inc. final).
    const tramos = suspensionesDelCaso(caso);
    let venceDespido: Date | null = null;
    let computoDespido = '';
    if (despido) {
      const tope = vencimientoHabil(despido, 90);
      const conSuspension = vencimientoHabilSuspendido(despido, 60, tramos);
      const topado = conSuspension > tope;
      venceDespido = topado ? tope : conSuspension;
      const detalle = tramos
        .map((t) => `del ${fmtDia(t.inicio)} (solicitud del comparendo) al ${fmtDia(t.fin)} (comparendo)`)
        .join('; ');
      computoDespido = '60 días hábiles desde la separación'
        + (detalle ? `, suspendidos ${detalle}` : '')
        + (topado ? '. Se aplica el tope de 90 días hábiles del art. 168 inc. final' : '');
    }

    if (despido && venceDespido) {
      const esAutodespido = motivos.includes(MOTIVO_AUTODESPIDO);

      if (!esAutodespido || motivos.includes(MOTIVO_DESPIDO)) {
        agregar('ct168', 'Demanda',
          'Demandar por despido injustificado, indebido o improcedente',
          'Art. 168 Código del Trabajo', computoDespido,
          despido, venceDespido);
      }
      // El art. 171 se remite al 168: mismo plazo y mismo cómputo, suspensión
      // incluida. No tiene reglas propias.
      if (esAutodespido) {
        agregar('ct171', 'Demanda',
          'Demandar por autodespido (despido indirecto)',
          'Art. 171 Código del Trabajo (se remite al art. 168)', computoDespido,
          despido, venceDespido);
      }
      if (motivos.includes(MOTIVO_TUTELA)) {
        agregar('ct489', 'Demanda',
          'Demandar por tutela de derechos fundamentales',
          'Art. 489 Código del Trabajo', computoDespido,
          despido, venceDespido);
      }
      if (motivos.includes(MOTIVO_COTIZACIONES)) {
        agregar('ct162', 'Demanda',
          'Demandar la nulidad del despido por cotizaciones impagas',
          'Arts. 162 y 510 Código del Trabajo',
          '6 meses corridos desde la suspensión de los servicios',
          despido, vencimientoMeses(despido, 6));
      }
      agregar('ct510', 'Demanda',
        'Prescripción de las acciones de actos y contratos',
        'Art. 510 inc. 2º Código del Trabajo',
        '6 meses corridos desde el término de los servicios',
        despido, vencimientoMeses(despido, 6));
    }

    if (caso.parteRepresentada === 'demandado') {
      const ap = proximaPreparatoria(caso, hoy);
      if (ap) {
        agregar('ct452', 'Contestación',
          'Contestar la demanda',
          'Art. 452 Código del Trabajo',
          `5 días hábiles antes de la audiencia preparatoria del ${fmtDia(ap)}`,
          ap, limiteAnterior(ap, 5));
      }
    }
  }

  if (esFamilia && caso.parteRepresentada === 'demandado') {
    const ap = proximaPreparatoria(caso, hoy);
    if (ap) {
      agregar('lf58', 'Contestación',
        'Contestar la demanda y, si corresponde, reconvenir',
        'Art. 58 Ley 19.968',
        `5 días hábiles antes de la audiencia preparatoria del ${fmtDia(ap)}`,
        ap, limiteAnterior(ap, 5));
    }
  }

  return plazos;
}

/** Los plazos de todos los casos: primero lo pendiente, del más urgente al
 *  menos urgente, y al final lo ya cumplido. Deja fuera los que vencieron hace
 *  más de un mes. */
export function plazosDeCasos(casos: any[], hoy: Date = new Date()): PlazoLegal[] {
  const corte = sumarDias(hoy, -DIAS_VISIBLE_TRAS_VENCER);
  return (casos || [])
    .flatMap((c) => plazosDelCaso(c, hoy))
    .filter((p) => p.vence >= corte)
    .sort((a, b) => {
      if (!!a.realizado !== !!b.realizado) return a.realizado ? 1 : -1;
      return a.vence.getTime() - b.vence.getTime();
    });
}

/** Semáforo del plazo. "vencido" y "urgente" son los que hay que mirar hoy. */
export function urgencia(p: PlazoLegal): 'realizado' | 'vencido' | 'urgente' | 'proximo' | 'holgado' {
  if (p.realizado) return 'realizado';
  if (p.dias < 0) return 'vencido';
  if (p.dias <= 5) return 'urgente';
  if (p.dias <= 15) return 'proximo';
  return 'holgado';
}
